import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Diamond,
  FolderOpen,
  Calendar,
  MapPin,
} from 'lucide-react';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { xamplinApi, entityIdOf, type XpStructureEvent } from '@/lib/api/xamplin';
import { cn } from '@/lib/cn';

/**
 * Brand-scoped tree shown in the Sidebar above the global nav. Renders the
 * currently selected brand plus an accordion of its Xamplin projects, their
 * events and stations.
 *
 * Data comes from the xp-* domain: `projects.list(brandId)` for the top level,
 * then `projects.structure(projectId)` lazily when a project is expanded (so we
 * never pull all 300+ events up front). Event groups are flattened into a single
 * events level to keep the tree shallow.
 *
 * Single-open behavior: only one project can be expanded at a time, and within
 * that project only one event — opening another collapses the previous.
 */
export function SidebarBrandTree({ expanded }: { expanded: boolean }) {
  const t = useT();
  const location = useLocation();
  const brandId = useSelectedBrandId();
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);

  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId ?? 0),
    queryFn: () => brandsApi.get(brandId!),
    enabled: !!brandId,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(brandId ?? 0),
    queryFn: () => xamplinApi.projects.list(brandId!),
    enabled: !!brandId,
  });
  // Structure of the currently-open project — gives its events + stations.
  const structureQ = useQuery({
    queryKey: queryKeys.xamplin.projectStructure(expandedProjectId ?? 0),
    queryFn: () => xamplinApi.projects.structure(expandedProjectId!),
    enabled: !!expandedProjectId,
  });

  // Flatten the open project's event groups into a single events list.
  const openProjectEvents = useMemo<XpStructureEvent[]>(() => {
    const groups = structureQ.data?.structured?.project?.eventGroups ?? [];
    return groups.flatMap((g) => g.events ?? []);
  }, [structureQ.data]);

  if (!brandId) return null;

  const brand = brandQ.data ?? null;
  const projects = projectsQ.data?.items ?? [];

  // Collapsed sidebar — just the brand logo as the anchor.
  if (!expanded) {
    return (
      <div className="my-2 flex justify-center">
        <Link
          to={`/brand/${brandId}`}
          title={brand?.name}
          className={cn(
            'grid h-9 w-9 place-items-center rounded-lg border transition-colors',
            location.pathname === `/brand/${brandId}`
              ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-10)]'
              : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]',
          )}
        >
          {brand?.logo ? (
            <img
              src={resolveImageUrl(brand.logo)}
              alt={brand.name ?? ''}
              className="h-6 w-6 rounded object-cover"
            />
          ) : (
            <Diamond className="h-4 w-4 text-[var(--color-xk-accent)]" />
          )}
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-2 mt-1 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-1.5">
      {/* Brand header — clickable, navigates to /brand/:id */}
      <Link
        to={`/brand/${brandId}`}
        className={cn(
          'flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
          location.pathname === `/brand/${brandId}`
            ? 'bg-[var(--color-accent-08)] text-[var(--color-xk-accent)]'
            : 'text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-bg-deep)]',
        )}
      >
        {brand?.logo ? (
          <img
            src={resolveImageUrl(brand.logo)}
            alt=""
            className="h-5 w-5 flex-none rounded object-cover"
          />
        ) : (
          <Diamond className="h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
        )}
        <span className="min-w-0 flex-1 truncate text-[12px] font-extrabold uppercase tracking-wider">
          {brand?.name ?? '—'}
        </span>
      </Link>

      {/* Projects accordion */}
      {projectsQ.isLoading ? (
        <div className="px-2 py-2 text-[10px] text-[var(--color-xk-text-muted)]">
          {t('common.loading')}
        </div>
      ) : projects.length === 0 ? (
        <div className="px-2 py-2 text-[10px] text-[var(--color-xk-text-muted)]">
          {t('sidebar.brandTree.noProjects')}
        </div>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5">
          {projects.map((p) => {
            const isProjectOpen = expandedProjectId === p.id;
            const isProjectActive = location.pathname === `/projects/${p.id}`;
            const events = isProjectOpen ? openProjectEvents : [];
            return (
              <li key={p.id}>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedProjectId(isProjectOpen ? null : p.id);
                      setExpandedEventId(null);
                    }}
                    aria-expanded={isProjectOpen}
                    aria-label={
                      isProjectOpen
                        ? t('sidebar.brandTree.collapse')
                        : t('sidebar.brandTree.expand')
                    }
                    className="grid h-6 w-5 flex-none place-items-center rounded text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
                  >
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-transform',
                        isProjectOpen && 'rotate-90',
                      )}
                    />
                  </button>
                  <Link
                    to={`/projects/${p.id}`}
                    title={p.name}
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] font-semibold transition-colors',
                      isProjectActive
                        ? 'bg-[var(--color-accent-08)] text-[var(--color-xk-accent)]'
                        : 'text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-bg-deep)]',
                    )}
                  >
                    <FolderOpen className="h-3 w-3 flex-none" />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  </Link>
                </div>
                {isProjectOpen && (
                  <ul className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--color-xk-border)] pl-2">
                    {structureQ.isLoading ? (
                      <li className="px-1.5 py-1 text-[10px] text-[var(--color-xk-text-muted)]">
                        {t('common.loading')}
                      </li>
                    ) : events.length === 0 ? (
                      <li className="px-1.5 py-1 text-[10px] text-[var(--color-xk-text-muted)]">
                        {t('sidebar.brandTree.noEvents')}
                      </li>
                    ) : (
                      events.map((e) => {
                        const eventId = entityIdOf(e.id);
                        const isEventOpen = expandedEventId === eventId;
                        const isEventActive =
                          location.pathname ===
                          `/projects/${p.id}/events/${eventId}`;
                        const stations = e.stations ?? [];
                        return (
                          <li key={e.id}>
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedEventId(isEventOpen ? null : eventId)
                                }
                                aria-expanded={isEventOpen}
                                aria-label={
                                  isEventOpen
                                    ? t('sidebar.brandTree.collapse')
                                    : t('sidebar.brandTree.expand')
                                }
                                className="grid h-6 w-5 flex-none place-items-center rounded text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
                              >
                                <ChevronRight
                                  className={cn(
                                    'h-3 w-3 transition-transform',
                                    isEventOpen && 'rotate-90',
                                  )}
                                />
                              </button>
                              <Link
                                to={`/projects/${p.id}/events/${eventId}`}
                                title={e.name}
                                className={cn(
                                  'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] transition-colors',
                                  isEventActive
                                    ? 'bg-[var(--color-accent-08)] text-[var(--color-xk-accent)]'
                                    : 'text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-bg-deep)]',
                                )}
                              >
                                <Calendar className="h-3 w-3 flex-none" />
                                <span className="min-w-0 flex-1 truncate">
                                  {e.name}
                                </span>
                              </Link>
                            </div>
                            {isEventOpen && (
                              <ul className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--color-xk-border)] pl-2">
                                {stations.length === 0 ? (
                                  <li className="px-1.5 py-1 text-[10px] text-[var(--color-xk-text-muted)]">
                                    {t('sidebar.brandTree.noStations')}
                                  </li>
                                ) : (
                                  stations.map((st) => {
                                    const stationId = entityIdOf(st.id);
                                    return (
                                      <li key={st.id}>
                                        {/* Stations are leaf nodes — the
                                            leading spacer keeps the icon
                                            aligned with the event row above. */}
                                        <div className="flex items-center">
                                          <span
                                            className="h-6 w-5 flex-none"
                                            aria-hidden
                                          />
                                          <Link
                                            to={`/stations/${stationId}`}
                                            title={st.name}
                                            className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-bg-deep)]"
                                          >
                                            <MapPin className="h-3 w-3 flex-none" />
                                            <span className="min-w-0 flex-1 truncate">
                                              {st.name}
                                            </span>
                                          </Link>
                                        </div>
                                      </li>
                                    );
                                  })
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
